/**
 * Utility functions for invoice calculations in the Eazy Fatoora application
 * These functions handle all the business logic for calculating totals, VAT, discounts, etc.
 */

/**
 * Calculate all totals for a purchase invoice item
 * Purchase Invoice Calculation Flow:
 * 1. Line Total = Quantity × Unit Price (cost price, excluding VAT)
 * 2. Discount Amount = Line Total × (Discount % / 100) OR user enters Discount Amount directly
 * 3. Net Value (After Discount) = Line Total - Discount Amount
 * 4. VAT Amount = Net Value × (VAT % / 100)
 * 5. Grand Total = Net Value + VAT Amount
 * 
 * Supports bidirectional discount calculation:
 * - If Discount % changes → calculates Discount Amount
 * - If Discount Amount changes → calculates Discount %
 * 
 * @param {Object} item - The purchase item with quantity, price, discount, etc.
 * @param {String} updatedField - The field that was just updated (for bidirectional calculations)
 * @returns {Object} The item with calculated totals
 */
export const calculatePurchaseItemTotals = (item, updatedField = null) => {
  // Create a copy of the item to work with
  let result = { ...item };
  
  // Convert values to numbers and ensure they're valid
  result.quantity = Number(result.quantity) || 0;
  result.price = Number(result.price) || 0;
  result.discount = Number(result.discount) || 0;
  result.vatRate = Number(result.vatRate) || 0;
  result.discountAmount = Number(result.discountAmount) || 0;
  
  // Step 1: Calculate Line Total (Quantity × Unit Price)
  result.lineTotal = result.quantity * result.price;
  
  // Step 2: Bidirectional Discount Calculation
  if (updatedField === 'discountAmount') {
    // User entered Discount Amount → Calculate Discount %
    if (result.lineTotal > 0) {
      result.discount = (result.discountAmount * 100) / result.lineTotal;
    } else {
      result.discount = 0;
    }
  } else {
    // User entered Discount % (or other field) → Calculate Discount Amount
    result.discountAmount = (result.lineTotal * result.discount) / 100;
  }
  
  // Step 3: Calculate Net Value (After Discount)
  result.afterDiscount = result.lineTotal - result.discountAmount;
  
  // Step 4: Calculate VAT Amount
  result.vatAmount = (result.afterDiscount * result.vatRate) / 100;
  
  // Step 5: Calculate Grand Total
  result.total = result.afterDiscount + result.vatAmount;
  
  // Format numeric values for display (2 decimal places)
  result.lineTotal = parseFloat(result.lineTotal.toFixed(2));
  result.discount = parseFloat(result.discount.toFixed(2));
  result.discountAmount = parseFloat(result.discountAmount.toFixed(2));
  result.afterDiscount = parseFloat(result.afterDiscount.toFixed(2));
  result.vatAmount = parseFloat(result.vatAmount.toFixed(2));
  result.total = parseFloat(result.total.toFixed(2));
  result.price = parseFloat(result.price.toFixed(2));
  
  return result;
};

/**
 * Calculate all totals for a sales invoice item with bidirectional support
 * Sales Invoice Calculation Flow (more complex with bidirectional calculations):
 * - Supports updating from Price + VAT
 * - Supports updating from Discount Amount
 * - Supports updating from Line Total
 * 
 * @param {Object} item - The invoice item with quantity, price, discount, etc.
 * @param {String} updatedField - The field that was just updated (for bidirectional calculations)
 * @returns {Object} The item with calculated totals
 */
export const calculateItemTotals = (item, updatedField = null) => {
  // Create a copy of the item to work with
  let result = { ...item };
  
  // Convert values to numbers and ensure they're valid
  result.quantity = Number(result.quantity) || 0;
  result.price = Number(result.price) || 0;
  result.discount = Number(result.discount) || 0;
  result.vatRate = Number(result.vatRate) || 0;
  result.discountAmount = Number(result.discountAmount) || 0;
  result.lineTotal = Number(result.lineTotal) || 0;
  result.priceWithVat = Number(result.priceWithVat) || 0;
  
  // Handle bidirectional calculations based on which field was updated
  if (updatedField === 'priceWithVat') {
    // Back-calculate price from priceWithVat
    result.price = result.priceWithVat / (1 + result.vatRate / 100);
  } else if (updatedField === 'discountAmount') {
    // Back-calculate discount percentage from discount amount
    result.discount = result.lineTotal > 0 ? (result.discountAmount * 100) / result.lineTotal : 0;
  } else if (updatedField === 'lineTotal') {
    // Back-calculate price from lineTotal and quantity
    if (result.quantity > 0) {
      result.price = result.lineTotal / result.quantity;
      // Update priceWithVat to match the new price
      result.priceWithVat = result.price * (1 + result.vatRate / 100);
    }
  }
  
  // Forward calculations (always performed)
  // Calculate line total (quantity * price)
  if (updatedField !== 'lineTotal') {
    result.lineTotal = result.quantity * result.price;
  }
  
  // Calculate discount amount if discount percentage was updated
  if (updatedField !== 'discountAmount') {
    result.discountAmount = (result.lineTotal * result.discount) / 100;
  }
  
  // Calculate amount after discount
  result.afterDiscount = result.lineTotal - result.discountAmount;
  
  // Calculate VAT amount
  result.vatAmount = (result.afterDiscount * result.vatRate) / 100;
  
  // Calculate total including VAT
  result.total = result.afterDiscount + result.vatAmount;
  
  // Calculate price with VAT included if price was updated
  if (updatedField !== 'priceWithVat') {
    result.priceWithVat = result.price * (1 + result.vatRate / 100);
  }
  
  // Format numeric values for display
  result.lineTotal = parseFloat(result.lineTotal.toFixed(2));
  result.discountAmount = parseFloat(result.discountAmount.toFixed(2));
  result.afterDiscount = parseFloat(result.afterDiscount.toFixed(2));
  result.vatAmount = parseFloat(result.vatAmount.toFixed(2));
  result.total = parseFloat(result.total.toFixed(2));
  result.price = parseFloat(result.price.toFixed(2));
  result.priceWithVat = parseFloat(result.priceWithVat.toFixed(2));
  
  return result;
};

/**
 * Calculate all totals for an entire invoice
 * @param {Array} items - Array of invoice items
 * @returns {Object} Object containing subtotal, discount, vatAmount, and total
 */
export const calculateInvoiceTotals = (items) => {
  // Initialize totals
  let subtotal = 0;
  let totalDiscount = 0;
  let totalVat = 0;
  let grandTotal = 0;
  
  // Calculate totals from all items
  items.forEach(item => {
    const calculatedItem = calculateItemTotals(item);
    
    subtotal += calculatedItem.lineTotal || 0;
    totalDiscount += calculatedItem.discountAmount || 0;
    totalVat += calculatedItem.vatAmount || 0;
    grandTotal += calculatedItem.total || 0;
  });
  
  return {
    subtotal,
    totalDiscount,
    totalVat,
    grandTotal,
    netAmount: subtotal - totalDiscount
  };
};

/**
 * Calculate total payments made on an invoice
 * @param {Array} payments - Array of payment objects
 * @returns {Number} Total amount paid
 */
export const calculateTotalPayments = (payments) => {
  return payments.reduce((total, payment) => total + (Number(payment.amount) || 0), 0);
};

/**
 * Calculate remaining balance on an invoice
 * @param {Array} items - Invoice items
 * @param {Array} payments - Payment records
 * @returns {Number} Remaining balance
 */
export const calculateRemainingBalance = (items, payments) => {
  const { grandTotal } = calculateInvoiceTotals(items);
  const totalPaid = calculateTotalPayments(payments);
  
  return Math.max(0, grandTotal - totalPaid);
};

/**
 * Generate a new invoice number
 * @returns {String} New invoice number
 */
export const generateInvoiceNumber = () => {
  return 'INV-' + Math.floor(10000 + Math.random() * 90000);
};

/**
 * Calculate due date based on current date and terms
 * @param {Number} daysToAdd - Number of days to add to current date
 * @returns {Date} Due date
 */
export const calculateDueDate = (daysToAdd = 30) => {
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + daysToAdd);
  return dueDate;
};

/**
 * Format currency value
 * @param {Number} value - Value to format
 * @param {Number} decimals - Number of decimal places
 * @returns {String} Formatted currency string
 */
export const formatCurrency = (value, decimals = 2) => {
  return `${(Number(value) || 0).toFixed(decimals)} SAR`;
};

/**
 * Update price with VAT or price without VAT based on the other value
 * @param {Object} params - Parameters
 * @param {Number} params.price - Price without VAT
 * @param {Number} params.priceWithVat - Price with VAT
 * @param {Number} params.vatRate - VAT rate percentage
 * @param {String} params.updatedField - Which field was updated ('price' or 'priceWithVat')
 * @returns {Object} Updated price and priceWithVat
 */
export const updatePriceWithVAT = ({ price, priceWithVat, vatRate, updatedField }) => {
  const vat = Number(vatRate) || 0;
  
  if (updatedField === 'price') {
    const newPrice = Number(price) || 0;
    const newPriceWithVat = newPrice * (1 + vat / 100);
    return { 
      price: parseFloat(newPrice.toFixed(2)), 
      priceWithVat: parseFloat(newPriceWithVat.toFixed(2)) 
    };
  } else {
    const newPriceWithVat = Number(priceWithVat) || 0;
    const newPrice = newPriceWithVat / (1 + vat / 100);
    return { 
      price: parseFloat(newPrice.toFixed(2)), 
      priceWithVat: parseFloat(newPriceWithVat.toFixed(2)) 
    };
  }
};

/**
 * Debounce function to limit the rate at which a function can fire
 * @param {Function} func - The function to debounce
 * @param {Number} wait - The debounce wait time in milliseconds
 * @returns {Function} Debounced function
 */
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

/**
 * Format a number to a specified number of decimal places
 * @param {Number} value - The value to format
 * @param {Number} decimals - Number of decimal places
 * @returns {String} Formatted number string
 */
export const formatNumber = (value, decimals = 2) => {
  const num = Number(value) || 0;
  return num.toFixed(decimals);
};

/**
 * Validate a numeric input value
 * @param {String|Number} value - Value to validate
 * @param {Object} options - Validation options
 * @param {Number} options.min - Minimum allowed value
 * @param {Number} options.max - Maximum allowed value
 * @param {Number} options.decimals - Maximum decimal places
 * @returns {Number} Validated number
 */
export const validateNumericInput = (value, { min = 0, max = Infinity, decimals = 2 } = {}) => {
  // Convert to number and handle NaN
  let num = Number(value);
  if (isNaN(num)) return min;
  
  // Apply min/max constraints
  num = Math.max(min, Math.min(max, num));
  
  // Apply decimal precision
  return parseFloat(num.toFixed(decimals));
};
